import React, { useState, useEffect } from "react";
import { ConfirmDeleteModal } from "../../../../../../Components/ConfirmDeleteModal";
import {
  TableColumnsType,
  Button,
  Space,
  Dropdown,
  Menu,
  Modal,
  Select,
  Tag,
  message,
  Spin,
  Pagination,
  Typography,
} from "antd";
import { Icon } from "@iconify/react";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../../../../../Components/DataTable/DataTable";
import { panelApiUrl } from "~/Utils";
import { api, panelApiBaseUrl, remove } from "../../../../../../../SPA/Services/api.service";
import { getFormattedDate, getKey } from "@utils/CommonUtils";
import { updatedAtOf, updatedByOf } from "../../../../../../Utils/auditFields";
import { apiSortField } from "../../../../../../Utils/sortField";
import styles from "@styles/variables.module.scss";

export const VendorList = () => {
  const navigate = useNavigate();

  const [vendorData, setVendorData] = useState<any[]>([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [deletingVendorId, setDeletingVendorId] = useState<string | null>(null);
  const [deleteVendorModal, setDeleteVendorModal] = useState(false);
  const [deleteVendorLoading, setDeleteVendorLoading] = useState(false);
  const [selectedVendorRegionFilter, setSelectedVendorRegionFilter] = useState<string | undefined>(undefined);
  const [vendorTotal, setVendorTotal] = useState<number>(0);
  const [vendorPage, setVendorPage] = useState<number>(0);
  const [vendorSearch, setVendorSearch] = useState<string>("");
  const [clearVendorSearch, setClearVendorSearch] = useState(false);
  const [vendorApiUrl, setVendorApiUrl] = useState<string>("");
  const [regionData, setRegionData] = useState<any[]>([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdOn");

  const recordsPerPage = 10;

  useEffect(() => {
    setVendorPage(0);
    fetchVendorData(vendorSearch, 0, sortOrder, orderBy);
  }, [selectedVendorRegionFilter]);

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = () => {
    api
      .get(`${panelApiBaseUrl}/region?page=0&size=1000`)
      .then((res: any) => {
        const responseData = res?.data?.response?.data?.records || res?.data?.data || [];
        const sortedData = [...responseData].sort((a: any, b: any) => (a.regionName || "").localeCompare(b.regionName || ""));
        setRegionData(sortedData);
      })
      .catch((err) => {
        console.error("Error fetching regions:", err);
      });
  };

  const handleVendorSearchChange = debounce((value: string, isLoading: boolean) => {
    setClearVendorSearch(false);
    setVendorSearch(value);
    setVendorPage(0);
    setVendorLoading(isLoading);
    fetchVendorData(value, 0, sortOrder, orderBy);
  }, 1000);

  const handleTableSort = (column: any, currentSortOrder: string) => {
    const dataIndex = column.dataIndex;
    let order = "";
    if (column.sorter) {
      if (orderBy === dataIndex) {
        if (currentSortOrder === "asc") {
          setSortOrder("desc");
          order = "desc";
        } else if (currentSortOrder === "desc") {
          setSortOrder("asc");
          order = "asc";
        } else {
          setSortOrder("desc");
          order = "desc";
        }
      } else {
        setSortOrder("desc");
        order = "desc";
      }
    }
    setOrderBy(dataIndex);
    setVendorPage(0);
    fetchVendorData(vendorSearch, 0, order, dataIndex);
  };

  const fetchVendorData = (searchText: string = "", currentPage: number = 0, currentOrder: string = sortOrder, currentOrderBy: string = orderBy) => {
    setVendorLoading(true);
    setVendorError(null);

    let url = `${panelApiUrl}/vendor/get-vendor-list?page=${currentPage}&size=${recordsPerPage}&searchText=${encodeURIComponent(
      searchText
    )}&order=${currentOrder}&orderBy=${apiSortField(currentOrderBy)}`;
    if (selectedVendorRegionFilter) {
      url += `&regionId=${selectedVendorRegionFilter}`;
    }

    setVendorApiUrl(url);

    api
      .get(url)
      .then((res: any) => {
        const responseData = res?.data?.response?.data;
        const records = responseData?.records || [];
        setVendorTotal(responseData?.totalCount || 0);

        const mappedData = records.map((item: any, index: any) => ({
          key: getKey(currentPage, index),
          id: item.id,
          regionName: item.region?.regionName || item.regionName || "-",
          vendorName: item.vendorName || "-",
          vendorStatus: item.active ? "Active" : "Inactive",
          activeContractCount: item.activeContractCount || 0,
          createdAt: getFormattedDate(item.createdOn) || "-",
          createdBy: item.createdBy || "-",
          modifiedAt: updatedAtOf(item),
          modifiedBy: updatedByOf(item),
          ...item,
        }));
        setVendorData(mappedData);

        const uniqueRegionsMap = new Map();
        records.forEach((record: any) => {
          const rId = record.regionId || record.region?.id;
          const rName = record.regionName || record.region?.regionName;
          if (rId && rName) {
            uniqueRegionsMap.set(rId, { id: rId, regionName: rName });
          }
        });
      })
      .catch((err: any) => {
        console.error("Error fetching vendor data:", err);
        setVendorData([]);
        setVendorTotal(0);
        setVendorError("Failed to fetch vendor data");
        message.error("Error fetching vendor data");
      })
      .finally(() => {
        setVendorLoading(false);
      });
  };

  const handleVendorMenuClick = (key: string, record: { [key in string]: any }) => {
    if (key === "edit") {
      navigate(`/workorder-manage-catalog/vendor-edit?id=${record.id}`);
    } else if (key === "delete") {
      setDeletingVendorId(record?.id);
      setDeleteVendorModal(true);
    }
  };

  const handleDeleteVendor = () => {
    setDeleteVendorLoading(true);
    remove(`${panelApiUrl}/vendor/${deletingVendorId}`)
      .then((res: any) => {
        setDeleteVendorLoading(false);
        message.success(res?.data?.message || "Vendor deleted successfully");
        setDeleteVendorModal(false);
        setDeletingVendorId(null);
        fetchVendorData(vendorSearch, vendorPage, sortOrder, orderBy);
      })
      .catch((err: any) => {
        setDeleteVendorLoading(false);
        message.error(err?.data?.message || "Failed to delete vendor");
        setDeleteVendorModal(false);
        setDeletingVendorId(null);
      });
  };


  const vendorColumns: TableColumnsType = [
    {
      title: "Region",
      dataIndex: "regionName",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "regionName" ? "ascend" : sortOrder === "desc" && orderBy === "regionName" ? "descend" : undefined,
    },
    {
      title: "Vendor name",
      dataIndex: "vendorName",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "vendorName" ? "ascend" : sortOrder === "desc" && orderBy === "vendorName" ? "descend" : undefined,
    },
    {
      title: "Vendor status",
      dataIndex: "active",
      sorter: false,
      sortOrder: sortOrder === "asc" && orderBy === "active" ? "ascend" : sortOrder === "desc" && orderBy === "active" ? "descend" : undefined,
      render: (value: any) => {
        return <Tag color={value ? "success" : "default"}>{value ? "Active" : "Inactive"}</Tag>;
      },
    },
    {
      title: "Active contract count",
      dataIndex: "activeContractCount",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "activeContractCount" ? "ascend" : sortOrder === "desc" && orderBy === "activeContractCount" ? "descend" : undefined,
    },
    {
      title: "Created At",
      dataIndex: "createdOn",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "createdOn" ? "ascend" : sortOrder === "desc" && orderBy === "createdOn" ? "descend" : undefined,
      render: (value: any) => getFormattedDate(value) || "-",
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "createdBy" ? "ascend" : sortOrder === "desc" && orderBy === "createdBy" ? "descend" : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updatedOn",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "updatedOn" ? "ascend" : sortOrder === "desc" && orderBy === "updatedOn" ? "descend" : undefined,
      render: (value: any) => getFormattedDate(value) || "-",
    },
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      sorter: true,
      sortOrder: sortOrder === "asc" && orderBy === "updatedBy" ? "ascend" : sortOrder === "desc" && orderBy === "updatedBy" ? "descend" : undefined,
    },
    {
      title: "Action",
      dataIndex: " ",
      width: 90,
      render: (value: any, record: any) => {
        const menuItems = [
          {
            label: (
              <Typography.Text
                type="secondary"
                style={{ color: styles?.primary }}
              >
                <Space>
                  <Icon icon="akar-icons:edit" inline fontSize={16} style={{ color: styles?.primary }} />
                  Edit
                </Space>
              </Typography.Text>
            ),
            key: "edit",
          },
          {
            label: (
              <Typography.Text
                type="danger"
              >
                <Space>
                  <Icon icon="fluent:delete-48-regular" inline fontSize={16} />
                  Delete
                </Space>
              </Typography.Text>
            ),
            key: "delete",
          },
        ];
        return (
          <Dropdown
            overlay={
              <Menu
                /* A portalled menu bubbles up the React tree, so the row
                   click handler on the cell would fire too. */
                onClick={({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  handleVendorMenuClick(key, record);
                }}
                items={menuItems}
              />
            }
            trigger={["click"]}
            placement="bottomLeft"
          >
            <span onClick={(e) => e.stopPropagation()}>
              <Icon icon={"bi:three-dots-vertical"} style={{ cursor: "pointer" }} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      {vendorLoading && vendorData.length === 0 ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : vendorError ? (
        <Typography.Text type="danger">{vendorError}</Typography.Text>
      ) : (
        <>
          <DataTable
            multiSelect={true}
            hideExport={false}
            /* The three-dots Action column is a menu, not data. */
            excludeExportCols={["Action"]}
            exportFileName="Vendor"
            exportApiUrl={vendorApiUrl}
            columns={vendorColumns?.map((column) => ({
              ...column,
              onHeaderCell: (column: any) => ({
                onClick: () => {
                  if (column.sorter) {
                    handleTableSort(column, sortOrder);
                  }
                },
              }),
            }))}
            tableData={vendorData}
            className="table-clickable"
            onClick={(record: any) => navigate(`/workorder-manage-catalog/vendor-edit?id=${record.id}&mode=view`)}
            searchPlaceholderText="Search by vendor name"
            searchQuery={vendorSearch}
            clearStringSearch={clearVendorSearch}
            onSearchChange={handleVendorSearchChange}
            inputDisable={vendorLoading}
            isPagination={true}
            isServerSearchApi={true}
            totalRecords={vendorTotal}
            recordsPerPage={recordsPerPage}
            additionalCTAToRight={true}
            additionalCTA={
              <Space size={8}>
                <Select
                  placeholder="Region"
                  allowClear
                  style={{ width: 150 }}
                  value={selectedVendorRegionFilter}
                  onChange={(value) => setSelectedVendorRegionFilter(value)}
                >
                  {regionData.map((region) => (
                    <Select.Option key={region.id} value={region.id}>
                      {region.regionName}
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  onClick={() => navigate("/workorder-manage-catalog/vendor-add")}
                >
                  <Space>
                    <Icon icon="akar-icons:plus" inline />
                    Add
                  </Space>
                </Button>
              </Space>
            }
          />
          <div className="pagination">
            <Pagination
              defaultCurrent={1}
              current={vendorPage + 1}
              total={vendorTotal}
              onChange={(newPage) => {
                const startIndex = newPage - 1;
                setVendorPage(startIndex);
                fetchVendorData(vendorSearch, startIndex, sortOrder, orderBy);
              }}
              showSizeChanger={false}
              pageSize={10}
            />
          </div>
        </>
      )}

      {/* Delete Vendor Modal */}
      <ConfirmDeleteModal
        open={deleteVendorModal}
        message="Are you sure you want to delete the vendor?"
        isConfirming={deleteVendorLoading}
        onCancel={() => {
          setDeleteVendorModal(false);
          setDeletingVendorId(null);
        }}
        onConfirm={handleDeleteVendor}
      />
    </>
  );
};
